/**
 * @fileoverview Form Component
 *
 * Generic form component that integrates with Formspree and reCAPTCHA.
 * Handles form submission, validation, and user feedback.
 *
 * SSR Exception: MUST use 'use client'
 * Reason: Uses form state, user interactions, and browser APIs.
 */
"use client";

import { useForm } from "@formspree/react";
import { isValidPhoneNumber } from "libphonenumber-js";
import * as React from "react";
import { z } from "zod";
import { useRecaptcha } from "@/components/global/recaptcha-provider";
import type { FileUploadRef } from "@/components/ui/file-upload";
import type { FormConfig } from "@/lib/config/forms";
import { pushDataLayerEvent } from "@/lib/gtm/data-layer";
import { cn } from "@/lib/utils";
import { FormField } from "./form-field";
import { FormSubmit } from "./form-submit";

/** Timeout for reCAPTCHA script loading (milliseconds) */
const RECAPTCHA_LOAD_TIMEOUT = 10000;

/** Timeout for reCAPTCHA token execution (milliseconds) */
const RECAPTCHA_EXECUTE_TIMEOUT = 5000;

/** Execute reCAPTCHA with timeout (timeout starts after ready) */
function executeRecaptcha(
	siteKey: string,
	timeout = RECAPTCHA_EXECUTE_TIMEOUT,
): Promise<string> {
	return new Promise((resolve, reject) => {
		window.grecaptcha.ready(async () => {
			const timeoutId = setTimeout(() => reject(new Error("timeout")), timeout);
			try {
				const token = await window.grecaptcha.execute(siteKey, {
					action: "contact_form",
				});
				clearTimeout(timeoutId);
				if (!token) reject(new Error("Empty token"));
				else resolve(token);
			} catch (error) {
				clearTimeout(timeoutId);
				reject(error);
			}
		});
	});
}

export interface FormProps {
	config: FormConfig;
	className?: string;
}

export function Form({ config, className }: FormProps) {
	const { isReady: isRecaptchaReady, siteKey: recaptchaSiteKey } =
		useRecaptcha();

	const [state, handleSubmit] = useForm(config.formspreeId);
	const [formData, setFormData] = React.useState<Record<string, string>>({});
	const [touchedFields, setTouchedFields] = React.useState<Set<string>>(
		new Set(),
	);
	const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
		{},
	);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [recaptchaError, setRecaptchaError] = React.useState<string | null>(
		null,
	);
	const formRef = React.useRef<HTMLFormElement>(null);
	const isSubmittingRef = React.useRef(false);

	// Create refs for file upload fields to access files on submission
	const fileUploadRefs = React.useRef<
		Map<string, React.RefObject<FileUploadRef | null>>
	>(new Map());

	// Initialize refs for file fields
	const getFileUploadRef = React.useCallback(
		(fieldName: string): React.RefObject<FileUploadRef | null> => {
			let ref = fileUploadRefs.current.get(fieldName);
			if (!ref) {
				ref = React.createRef<FileUploadRef | null>();
				fileUploadRefs.current.set(fieldName, ref);
			}
			return ref;
		},
		[],
	);

	// Detect if reCAPTCHA fails to load and show clear error message
	React.useEffect(() => {
		if (!config.enableRecaptcha) return;

		const timeoutId = setTimeout(() => {
			if (!isRecaptchaReady) {
				setRecaptchaError(
					"Security verification failed to load. Please refresh the page and try again. If the problem persists, check your browser settings or contact support.",
				);
			}
		}, RECAPTCHA_LOAD_TIMEOUT);

		return () => clearTimeout(timeoutId);
	}, [config.enableRecaptcha, isRecaptchaReady]);

	// Fire a single GTM dataLayer event once the form is confirmed submitted.
	// Wrapped via pushDataLayerEvent which swallows errors — must never block
	// the user-facing success state from rendering.
	React.useEffect(() => {
		if (!state.succeeded) return;
		pushDataLayerEvent({
			event: "lead_form_submission",
			form_id: config.id,
		});
	}, [state.succeeded, config.id]);

	// Validate a single field using Zod validators for built-in types
	const validateField = (name: string, value: string): string | undefined => {
		const field = config.fields.find((f) => f.name === name);
		if (!field?.validation) return undefined;

		const { required, minLength, maxLength, customMessage } = field.validation;

		if (required && !value.trim()) {
			return customMessage || `${field.label || field.name} is required`;
		}

		// Use Zod's built-in validator for email (safer than regex)
		if (field.type === "email" && value.trim()) {
			try {
				z.string().email().parse(value);
			} catch {
				return customMessage || "Please enter a valid email address";
			}
		}

		// Use libphonenumber-js for phone validation (handles all AU formats)
		if (field.type === "tel" && value.trim()) {
			if (!isValidPhoneNumber(value, "AU")) {
				return customMessage || "Please enter a valid Australian phone number";
			}
		}

		if (minLength && value.length < minLength) {
			return (
				customMessage ||
				`${field.label || field.name} must be at least ${minLength} characters`
			);
		}

		if (maxLength && value.length > maxLength) {
			return (
				customMessage ||
				`${field.label || field.name} must be at most ${maxLength} characters`
			);
		}

		return undefined;
	};

	// Validate all fields, returns errors object for immediate use
	const validateForm = (
		values: Record<string, string>,
	): Record<string, string> => {
		const errors: Record<string, string> = {};

		config.fields.forEach((field) => {
			const value = values[field.name] || "";
			const error = validateField(field.name, value);
			if (error) {
				errors[field.name] = error;
			}
		});

		setFieldErrors(errors);
		return errors;
	};

	const handleFieldChange = React.useCallback((name: string, value: string) => {
		setFormData((prev) => ({ ...prev, [name]: value }));
	}, []);

	const handleFieldChangeWithValidation = (name: string, value: string) => {
		handleFieldChange(name, value);

		if (touchedFields.has(name) && fieldErrors[name]) {
			const error = validateField(name, value);
			setFieldErrors((prev) => {
				if (error) {
					return { ...prev, [name]: error };
				}
				const { [name]: _, ...rest } = prev;
				return rest;
			});
		}
	};

	const handleFieldBlur = (name: string) => {
		setTouchedFields((prev) => new Set(prev).add(name));
		const error = validateField(name, formData[name] || "");
		if (error) {
			setFieldErrors((prev) => ({ ...prev, [name]: error }));
		}
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		// Synchronous race condition protection using ref
		// This prevents rapid clicks from bypassing the check before React re-renders
		if (isSubmittingRef.current || state.submitting) {
			return;
		}

		isSubmittingRef.current = true;
		setIsSubmitting(true);

		try {
			const form = event.currentTarget;
			const formDataObj = new FormData(form);

			const formValues: Record<string, string> = {};
			for (const [key, value] of formDataObj.entries()) {
				if (typeof value === "string") {
					formValues[key] = value;
				}
			}

			const errors = validateForm(formValues);
			const hasErrors = Object.keys(errors).length > 0;
			if (hasErrors) {
				const firstErrorField = config.fields.find(
					(field) => errors[field.name],
				);
				if (firstErrorField) {
					const element = form.querySelector(
						`[name="${firstErrorField.name}"]`,
					) as HTMLElement;
					element?.focus();
				}
				isSubmittingRef.current = false;
				setIsSubmitting(false);
				return;
			}

			let recaptchaToken = "";

			if (config.enableRecaptcha) {
				if (!window.grecaptcha || !recaptchaSiteKey) {
					setRecaptchaError(
						"Security verification unavailable. Please refresh and try again.",
					);
					isSubmittingRef.current = false;
					setIsSubmitting(false);
					return;
				}

				try {
					recaptchaToken = await executeRecaptcha(recaptchaSiteKey);
					setRecaptchaError(null);
				} catch {
					setRecaptchaError(
						"Security verification failed. Please refresh and try again.",
					);
					isSubmittingRef.current = false;
					setIsSubmitting(false);
					return;
				}
			}

			if (formRef.current) {
				// Check if any file upload has validation errors
				const fileFields = config.fields.filter((f) => f.type === "file");
				for (const fileField of fileFields) {
					const fileRef = fileUploadRefs.current.get(fileField.name);
					if (fileRef?.current?.hasErrors()) {
						// Focus the file upload area and let existing error display
						const element = formRef.current.querySelector(
							`[name="${fileField.name}"]`,
						)?.parentElement as HTMLElement;
						element?.scrollIntoView({ behavior: "smooth", block: "center" });
						isSubmittingRef.current = false;
						setIsSubmitting(false);
						return;
					}
				}

				// Create FormData and manually add files from refs
				// This is more robust than relying on native file input
				const freshFormData = new FormData(formRef.current);

				// Remove any existing file entries (from native input) and add from refs
				for (const fileField of fileFields) {
					const fileRef = fileUploadRefs.current.get(fileField.name);
					const files = fileRef?.current?.getFiles() || [];

					// Delete existing entries for this field
					freshFormData.delete(fileField.name);

					// Add files from ref
					for (const file of files) {
						freshFormData.append(fileField.name, file, file.name);
					}
				}

				if (recaptchaToken) {
					freshFormData.append("g-recaptcha-response", recaptchaToken);
				}

				await handleSubmit(freshFormData);
			}
		} catch {
			setRecaptchaError("An unexpected error occurred. Please try again.");
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		} finally {
			isSubmittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	if (state.succeeded) {
		return (
			<div
				className={cn(
					"rounded-lg border border-green-200 bg-green-50 p-6",
					className,
				)}
				role="alert"
			>
				<h3 className="text-lg font-semibold text-green-900">
					{config.successHeading}
				</h3>
				<p className="mt-2 text-green-800">{config.successMessage}</p>
			</div>
		);
	}

	return (
		<form
			ref={formRef}
			onSubmit={handleFormSubmit}
			className={cn("space-y-6", className)}
			encType="multipart/form-data"
			noValidate
		>
			<input
				type="text"
				name={config.honeypotField}
				style={{
					position: "absolute",
					left: "-5000px",
					height: "0",
					width: "0",
					zIndex: -1,
					opacity: 0,
					pointerEvents: "none",
				}}
				tabIndex={-1}
				autoComplete="off"
				aria-hidden="true"
			/>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				{config.fields.map((field) => (
					<div
						key={field.id}
						className={
							field.type === "textarea" ||
							field.type === "select" ||
							field.type === "file"
								? "sm:col-span-2"
								: ""
						}
					>
						<FormField
							field={field}
							value={formData[field.name] || ""}
							onChange={(value) =>
								handleFieldChangeWithValidation(field.name, value)
							}
							onBlur={() => handleFieldBlur(field.name)}
							error={
								touchedFields.has(field.name)
									? fieldErrors[field.name]
									: undefined
							}
							setError={(error) => {
								if (error) {
									setFieldErrors((prev) => {
										// Skip if error is already set to this value
										if (prev[field.name] === error) return prev;
										return { ...prev, [field.name]: error };
									});
									setTouchedFields((prev) => {
										// Skip if already touched
										if (prev.has(field.name)) return prev;
										return new Set(prev).add(field.name);
									});
								} else {
									setFieldErrors((prev) => {
										// Skip if there's no error to remove
										if (!(field.name in prev)) return prev;
										const { [field.name]: _, ...rest } = prev;
										return rest;
									});
								}
							}}
							fileUploadRef={
								field.type === "file" ? getFileUploadRef(field.name) : undefined
							}
						/>
					</div>
				))}
			</div>

			{recaptchaError && (
				<div
					className="rounded-lg border border-red-200 bg-red-50 p-4"
					role="alert"
				>
					<p className="text-sm font-semibold text-red-900">
						Security Verification Error
					</p>
					<p className="mt-1 text-sm text-red-800">{recaptchaError}</p>
				</div>
			)}

			{state.errors && (
				<div
					className="rounded-lg border border-red-200 bg-red-50 p-4"
					role="alert"
				>
					<p className="text-sm font-medium text-red-900">
						{config.errorMessage}
					</p>
					<ul className="mt-2 list-disc pl-5 text-sm text-red-800">
						{state.errors.getFormErrors().map((error, index: number) => (
							<li key={`${error.message}-${index}`}>{error.message}</li>
						))}
					</ul>
				</div>
			)}

			<div className="flex items-center justify-between">
				<FormSubmit
					loading={isSubmitting || state.submitting}
					disabled={
						isSubmitting ||
						state.submitting ||
						(config.enableRecaptcha && !isRecaptchaReady) ||
						!!recaptchaError
					}
					submitText={config.submitText}
					loadingText={
						config.enableRecaptcha && !isRecaptchaReady
							? "Loading security verification..."
							: config.loadingText
					}
				/>
			</div>

			{config.enableRecaptcha && (
				<p className="text-xs text-muted-foreground">
					Protected by reCAPTCHA.{" "}
					<a
						href="https://policies.google.com/privacy"
						target="_blank"
						rel="noopener noreferrer"
						className="underline hover:text-foreground"
					>
						Privacy
					</a>{" "}
					and{" "}
					<a
						href="https://policies.google.com/terms"
						target="_blank"
						rel="noopener noreferrer"
						className="underline hover:text-foreground"
					>
						Terms
					</a>
				</p>
			)}
		</form>
	);
}
